<script setup lang="ts">
import {ProgramSnapshot} from "@/program/interfaces/Program";
import BaseCard from "@/core/components/base/BaseCard.vue";
import ProgramTeaser from "@/program/components/program-teaser/ProgramTeaser.vue";
import ProgramLogo from "@/program/components/program-logo/ProgramLogo.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {Plus} from "@lucide/vue";
import {Appointment} from "@/core/interfaces/Appointment";
import AgendaAppointmentDetail from "@/agenda/components/AgendaAppointmentDetail.vue";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {toAgendaAppointment} from "@/agenda/lib/toAgendaAppointment";
import {ref} from "vue";
import StartJourneyModal
    from "@/appointment/components/consumer-appointment-timeline/start-journey/StartJourneyModal.vue";
import Customer from "@/customer/interfaces/Customer";
import dateHelper from "@/core/helpers/dateHelper";

const emit = defineEmits<{
    (e: 'refresh'): void;
    (e: 'openCertification'): void
}>();
const props = defineProps<{
    program: ProgramSnapshot
    appointment?: Appointment | null;
    customer: Customer;
}>();

const appointmentDialogOpen = ref(false);
const journeyDialogOpen = ref(false);

/**
 * Taken when the card is clicked: the card shows the program's next
 * appointment, which a refetch may replace while a dialog acts on this one.
 */
const openedAppointment = ref<AgendaAppointment | null>(null);

const openAppointment = () => {
    if (props.appointment) {
        openedAppointment.value = toAgendaAppointment(props.appointment);
        appointmentDialogOpen.value = true;
    }
};

/**
 * Open the appointment journey modal.
 */
const handleJourneyModalOpen = (value: boolean) => {
    if (!value) {
        emit('refresh');
    }

    journeyDialogOpen.value = value;
};
</script>

<template>
  <div class="pb-3">
    <BaseCard class="overflow-visible">
      <div
        class="flex flex-1 gap-x-4 items-center px-4 py-2 relative"
      >
        <ProgramLogo
          :program="program"
          :size="75"
          :shadow="false"
        />
        <ProgramTeaser
          :program="program"
          class="text-sm max-w-72"
          remove-action
        />
        <BaseButton
          density="comfortable"
          class="!absolute right-2 -bottom-3" :class="{
            'bg-primary text-white': !appointment,
            'bg-secondary text-primary': appointment
          }"
          @click="appointment ? openAppointment() : journeyDialogOpen = true"
        >
          <template v-if="!appointment">
            <Plus class="size-4 stroke-[4px] mr-2" />
            Planifier un RDV
          </template>
          <template v-else>
            ✅ RDV {{ dateHelper.relativeFormat(appointment.start_at) }}
          </template>
        </BaseButton>
      </div>
    </BaseCard>
    <!-- Mounted on the first click: its dialogs load the team and the types. -->
    <AgendaAppointmentDetail
      v-if="openedAppointment"
      v-model:open="appointmentDialogOpen"
      :appointment="openedAppointment"
      @changed="emit('refresh')"
    />
    <div v-if="!appointment">
      <StartJourneyModal
        :open="journeyDialogOpen"
        :customer="customer"
        :support-program-id="program.programmable_id"
        @update:open="handleJourneyModalOpen"
        @open-certification-dialog="emit('openCertification'); journeyDialogOpen = false;"
      />
    </div>
  </div>
</template>

<style scoped>

</style>
